import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { AssignmentCategory, AssignmentPointsSettings } from '../../types/assignmentPointsNote';

const UNIT_OPTIONS = [1, 10] as const;
const PRESET_COLORS = [
  '#FF316B',
  '#FFCC6E',
  '#8B5CF6',
  '#227971',
  '#65A994',
  '#3B82F6',
  '#F97316',
  '#EC4899',
];

function newId(): string {
  return `cat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function nearestUnit(n: number): (typeof UNIT_OPTIONS)[number] {
  if (n >= 5) return 10;
  return 1;
}

type Props = {
  open: boolean;
  settings: AssignmentPointsSettings;
  onClose: () => void;
  onSave: (settings: AssignmentPointsSettings) => void;
};

const SortableCategoryRow: React.FC<{
  cat: AssignmentCategory;
  canDelete: boolean;
  onRename: (id: string, name: string) => void;
  onColor: (id: string, color: string) => void;
  onDelete: (id: string) => void;
}> = ({ cat, canDelete, onRename, onColor, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: cat.id,
  });
  const colorRef = useRef<HTMLInputElement>(null);
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="cp-apn-set-cat-row">
      <input
        className="cp-apn-set-cat-name"
        value={cat.name}
        onChange={(e) => onRename(cat.id, e.target.value)}
      />
      <div className="cp-apn-set-color-wrap">
        <button
          type="button"
          className="cp-apn-set-color-dot"
          style={{ background: cat.color }}
          title="색상 변경"
          onClick={() => colorRef.current?.click()}
          aria-label="색상 변경"
        />
        <input
          ref={colorRef}
          type="color"
          className="cp-apn-set-color-hidden"
          value={cat.color}
          onChange={(e) => onColor(cat.id, e.target.value)}
        />
      </div>
      <button
        type="button"
        className="cp-apn-set-icon-btn"
        disabled={!canDelete}
        onClick={() => onDelete(cat.id)}
        title="삭제"
        aria-label="카테고리 삭제"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M3 6h18M8 6V4h8v2M9 10v8M15 10v8M5 6l1 14h12l1-14"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <button
        type="button"
        className="cp-apn-set-drag-btn"
        title="순서 변경"
        aria-label="드래그하여 순서 변경"
        {...attributes}
        {...listeners}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

const AssignmentPointsSettingsModal: React.FC<Props> = ({ open, settings, onClose, onSave }) => {
  const [unitPoints, setUnitPoints] = useState<(typeof UNIT_OPTIONS)[number]>(10);
  const [delayedPct, setDelayedPct] = useState('0');
  const [exemptedPct, setExemptedPct] = useState('0');
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const newColorInputRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (!open) return;
    setUnitPoints(nearestUnit(settings.defaultPoints || settings.statusDefaultPoints.confirmed || 10));
    setDelayedPct(String(settings.statusDefaultPoints.delayed ?? 0));
    setExemptedPct(String(settings.statusDefaultPoints.exempted ?? 0));
    setCategories(settings.categories.map((c) => ({ ...c })));
    setNewName('');
    setNewColor(PRESET_COLORS[0]);
    setColorPickerOpen(false);
  }, [open, settings]);

  if (!open) return null;

  const addCategory = () => {
    const name = newName.trim();
    if (!name) return;
    setCategories((prev) => [...prev, { id: newId(), name, color: newColor }]);
    setNewName('');
  };

  const handleSave = () => {
    const cats =
      categories.length > 0
        ? categories
        : [{ id: newId(), name: '기본', color: '#10504a' }];
    const delayed = Math.min(100, Math.max(0, Number(delayedPct) || 0));
    const exempted = Math.min(100, Math.max(0, Number(exemptedPct) || 0));
    onSave({
      defaultPoints: unitPoints,
      statusDefaultPoints: {
        pending: 0,
        confirmed: unitPoints,
        delayed,
        exempted,
      },
      categories: cats,
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCategories((prev) => {
      const oldIndex = prev.findIndex((c) => c.id === active.id);
      const newIndex = prev.findIndex((c) => c.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cp-apn-modal cp-apn-set-modal"
        role="dialog"
        aria-modal="true"
        aria-label="과제 관리 설정"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-apn-modal-header">
          <h2>과제 관리 설정</h2>
          <button type="button" className="cp-apn-icon-btn" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div className="cp-apn-modal-body cp-apn-set-body">
          {/* 기본 단위 설정 */}
          <section className="cp-apn-set-panel">
            <h3 className="cp-apn-set-pill-title">기본 단위 설정</h3>

            <div className="cp-apn-set-block">
              <p className="cp-apn-set-label">기본 단위 포인트</p>
              <div className="cp-apn-set-unit-row">
                {UNIT_OPTIONS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`cp-apn-set-unit-btn${unitPoints === n ? ' is-selected' : ''}`}
                    onClick={() => setUnitPoints(n)}
                  >
                    {n}점
                  </button>
                ))}
              </div>
            </div>

            <hr className="cp-apn-set-divider" />

            <div className="cp-apn-set-block">
              <p className="cp-apn-set-label">과제 상태별 기본 지급 포인트 설정</p>
              <div className="cp-apn-set-status-grid">
                <div className="cp-apn-set-status-card">
                  <div className="cp-apn-set-status-head">
                    <span className="cp-apn-status-dot" style={{ background: '#FF316B' }}>
                      1
                    </span>
                    <span>미확인</span>
                  </div>
                  <p className="cp-apn-set-status-desc">포인트 미지급</p>
                </div>
                <div className="cp-apn-set-status-card">
                  <div className="cp-apn-set-status-head">
                    <span className="cp-apn-status-dot" style={{ background: '#227971' }}>
                      2
                    </span>
                    <span>확인</span>
                  </div>
                  <p className="cp-apn-set-status-desc">과제별 설정</p>
                </div>
                <div className="cp-apn-set-status-card">
                  <div className="cp-apn-set-status-head">
                    <span className="cp-apn-status-dot" style={{ background: '#65A994' }}>
                      3
                    </span>
                    <span>지연확인 (%)</span>
                  </div>
                  <input
                    className="cp-apn-set-pct-input"
                    type="number"
                    min={0}
                    max={100}
                    value={delayedPct}
                    onChange={(e) => setDelayedPct(e.target.value)}
                    onBlur={() => {
                      if (delayedPct === '') setDelayedPct('0');
                      else setDelayedPct(String(Math.min(100, Math.max(0, Number(delayedPct) || 0))));
                    }}
                  />
                  <p className="cp-apn-set-status-hint">확인 포인트의 n% 지급 (반올림)</p>
                </div>
                <div className="cp-apn-set-status-card">
                  <div className="cp-apn-set-status-head">
                    <span
                      className="cp-apn-status-dot"
                      style={{ background: '#FFCC6E', color: '#1f1703' }}
                    >
                      4
                    </span>
                    <span>예외 (%)</span>
                  </div>
                  <input
                    className="cp-apn-set-pct-input"
                    type="number"
                    min={0}
                    max={100}
                    value={exemptedPct}
                    onChange={(e) => setExemptedPct(e.target.value)}
                    onBlur={() => {
                      if (exemptedPct === '') setExemptedPct('0');
                      else setExemptedPct(String(Math.min(100, Math.max(0, Number(exemptedPct) || 0))));
                    }}
                  />
                  <p className="cp-apn-set-status-hint">확인 포인트의 n% 지급 (반올림)</p>
                </div>
              </div>
            </div>
          </section>

          {/* 과제 카테고리 설정 */}
          <section className="cp-apn-set-panel">
            <h3 className="cp-apn-set-pill-title">과제 카테고리 설정</h3>

            <div className="cp-apn-set-block cp-apn-set-block--start">
              <p className="cp-apn-set-subhead">과제 카테고리 생성</p>
              <div className="cp-apn-set-create-row">
                <input
                  className="cp-apn-set-create-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="새 카테고리 이름"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCategory();
                    }
                  }}
                />
                <div className="cp-apn-set-color-wrap">
                  <button
                    type="button"
                    className="cp-apn-set-color-dot"
                    style={{ background: newColor }}
                    title="색상 선택"
                    onClick={() => setColorPickerOpen((v) => !v)}
                    aria-label="색상 선택"
                  />
                  <input
                    ref={newColorInputRef}
                    type="color"
                    className="cp-apn-set-color-hidden"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                  />
                  {colorPickerOpen ? (
                    <div className="cp-apn-set-color-popup">
                      {PRESET_COLORS.map((c) => (
                        <button
                          key={c}
                          type="button"
                          className={`cp-apn-set-color-opt${newColor === c ? ' is-on' : ''}`}
                          style={{ background: c }}
                          onClick={() => {
                            setNewColor(c);
                            setColorPickerOpen(false);
                          }}
                          aria-label={c}
                        />
                      ))}
                      <button
                        type="button"
                        className="cp-apn-set-color-custom"
                        onClick={() => {
                          newColorInputRef.current?.click();
                          setColorPickerOpen(false);
                        }}
                      >
                        직접
                      </button>
                    </div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="cp-apn-set-add-btn"
                  onClick={addCategory}
                  title="추가"
                  aria-label="카테고리 추가"
                >
                  +
                </button>
              </div>
            </div>

            <div className="cp-apn-set-block cp-apn-set-block--start">
              <p className="cp-apn-set-subhead">생성된 과제 카테고리</p>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext
                  items={categories.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="cp-apn-set-cat-list">
                    {categories.map((c) => (
                      <SortableCategoryRow
                        key={c.id}
                        cat={c}
                        canDelete={categories.length > 1}
                        onRename={(id, name) =>
                          setCategories((prev) =>
                            prev.map((x) => (x.id === id ? { ...x, name } : x))
                          )
                        }
                        onColor={(id, color) =>
                          setCategories((prev) =>
                            prev.map((x) => (x.id === id ? { ...x, color } : x))
                          )
                        }
                        onDelete={(id) =>
                          setCategories((prev) => prev.filter((x) => x.id !== id))
                        }
                      />
                    ))}
                    {categories.length === 0 ? (
                      <p className="cp-apn-empty-hint">카테고리가 없습니다.</p>
                    ) : null}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          </section>
        </div>

        <footer className="cp-pin-modal-footer">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={onClose}>
            취소
          </button>
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--primary" onClick={handleSave}>
            적용
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default AssignmentPointsSettingsModal;
